import { useState } from 'react';
import { PageShell } from '../PageShell/index';
import emailjs from '@emailjs/browser';

export function DealerPartnership() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    state: '',
    district: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.firstName.trim()) next.firstName = 'First name is required.';
    if (!form.phone.trim()) next.phone = 'Phone number is required.';
    if (!/^[0-9+\-\s]{8,16}$/.test(form.phone.trim())) next.phone = 'Enter a valid phone number.';
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (!form.state.trim()) next.state = 'State is required.';
    if (!form.district.trim()) next.district = 'District is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess('');
    setGlobalError('');
    if (!validate()) return;
    setLoading(true);

    try {
      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'service_14b6w7d';
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'template_86ysjrc';
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ''; 

      if (!publicKey) {
        throw new Error('EmailJS Public Key is missing. Please add it to your .env file.');
      }

      await emailjs.send(serviceId, templateId, form, publicKey);

      setSuccess('Thank you. Your partnership request has been sent successfully.');
      setForm({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        state: '',
        district: '',
        message: ''
      });
    } catch (err: any) {
      console.error(err);
      setGlobalError(err.message || 'Could not submit enquiry right now. Please call or WhatsApp our team directly.');
    } finally {
      setLoading(false);
    }
  };

  const dealerSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': 'Zfast Agritech India - Dealer & Distribution Network',
    'image': 'https://zfastagritechindia.com/images/global/zfast-logo.webp',
    'telephone': '+91 98120 13300',
    'email': 'info@zfastagritechindia.com',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'IN',
      'streetAddress': '503, Shree GD Tower, Vaishali Nagar, Ajmer Road',
      'addressLocality': 'Jaipur',
      'addressRegion': 'Rajasthan'
    },
    'url': 'https://zfastagritechindia.com/dealer-partnership',
    'areaServed': 'IN'
  };

  return (
    <PageShell 
      title="Dealer / Distributor Partnership" 
      eyebrow="Partnership" 
      path="/dealer-partnership" 
      description="The company welcomes feed dealers, edible oil distributors, retailers, agriculture networks, and bulk suppliers for long-term product conversations." 
      hideHeader
      jsonLd={dealerSchema}
    >
      <style>{`
        :root {
          --green: #16733a;
          --green-2: #25a956;
          --navy: #062f49;
          --dark: #10261a;
          --text: #5f7067;
          --soft: #f4faf3;
          --line: #dcebdd;
          --white: #ffffff;
          --shadow: 0 22px 55px rgba(6, 47, 73, 0.10);
          --radius: 22px;
        }

        .zfast-dealer {
          position: relative;
          padding: 76px 20px;
          overflow: hidden;
          background: linear-gradient(180deg, #ffffff 0%, #f6fbf5 100%);
          color: var(--dark);
        }

        .zfast-dealer::before {
          content: "";
          position: absolute;
          inset: 290px 0 0 0;
          background-image:
            linear-gradient(rgba(22, 115, 58, 0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(22, 115, 58, 0.045) 1px, transparent 1px);
          background-size: 44px 44px;
          pointer-events: none;
        }

        .zfast-dealer__wrap {
          position: relative;
          z-index: 1;
          max-width: 1200px;
          margin: 0 auto;
        }

        .zfast-dealer__top {
          display: grid;
          grid-template-columns: 1fr 360px;
          gap: 30px;
          align-items: end;
          margin-bottom: 42px;
        }

        .zfast-kicker {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
          color: var(--green);
          font-size: 13px;
          font-weight: 900;
          letter-spacing: 4px;
          text-transform: uppercase;
        }

        .zfast-kicker::before {
          content: "";
          width: 36px;
          height: 3px;
          background: var(--green);
          border-radius: 8px;
        }

        .zfast-dealer h1 {
          margin: 0 0 18px;
          font-size: clamp(44px, 7vw, 78px);
          line-height: 0.95;
          letter-spacing: -3px;
          color: var(--dark);
        }

        .zfast-dealer__intro {
          max-width: 790px;
          margin: 0;
          font-size: 18px;
          line-height: 1.75;
          color: var(--text);
        }

        .zfast-top-card {
          padding: 22px;
          border-radius: var(--radius);
          background: linear-gradient(135deg, var(--navy), #0b4968);
          color: #ffffff;
          box-shadow: var(--shadow);
          border-left: 6px solid var(--green-2);
        }

        .zfast-top-card strong {
          display: block;
          font-size: 22px;
          margin-bottom: 8px;
        }

        .zfast-top-card span {
          display: block;
          font-size: 14px;
          line-height: 1.6;
          color: rgba(255,255,255,0.78);
        }

        .zfast-dealer__layout {
          display: grid;
          grid-template-columns: 0.78fr 1.22fr;
          gap: 34px;
          align-items: start;
        }

        .zfast-info-panel,
        .zfast-form-panel {
          border: 1px solid var(--line);
          background: rgba(255,255,255,0.92);
          border-radius: 28px;
          box-shadow: var(--shadow);
          overflow: hidden;
        }

        .zfast-info-panel {
          padding: 24px;
        }

        .zfast-info-heading {
          padding: 24px;
          border-radius: 22px;
          background: #eef8f0;
          border-left: 5px solid var(--green);
          margin-bottom: 18px;
        }

        .zfast-info-heading h2 {
          margin: 0 0 8px;
          font-size: 26px;
          color: var(--dark);
          letter-spacing: -0.5px;
        }

        .zfast-info-heading p {
          margin: 0;
          color: var(--text);
          line-height: 1.6;
          font-size: 15px;
        }

        .zfast-dealer-card {
          display: grid;
          grid-template-columns: 54px 1fr;
          gap: 16px;
          padding: 20px;
          border-radius: 20px;
          border: 1px solid #e2efe4;
          background: #ffffff;
          margin-bottom: 14px;
          text-decoration: none;
          color: inherit;
          transition: 0.25s ease;
        }

        .zfast-dealer-card:hover {
          transform: translateY(-3px);
          border-color: rgba(22, 115, 58, 0.32);
          box-shadow: 0 16px 36px rgba(6, 47, 73, 0.09);
        }

        .zfast-dealer-card__icon {
          width: 54px;
          height: 54px;
          display: grid;
          place-items: center;
          border-radius: 16px;
          background: #eef8f0;
          color: var(--green);
          border: 1px solid #d8eadb;
        }

        .zfast-dealer-card__icon svg {
          width: 27px;
          height: 27px;
          stroke: currentColor;
        }

        .zfast-dealer-card h3 {
          margin: 2px 0 8px;
          font-size: 19px;
          color: var(--dark);
        }

        .zfast-dealer-card p,
        .zfast-dealer-card span {
          margin: 0;
          font-size: 15px;
          color: var(--text);
          line-height: 1.6;
          word-break: break-word;
        }

        .zfast-whatsapp {
          background: linear-gradient(135deg, #16733a, #25a956);
          border: 0;
          color: #ffffff;
        }

        .zfast-whatsapp .zfast-dealer-card__icon {
          background: rgba(255,255,255,0.16);
          color: #ffffff;
          border-color: rgba(255,255,255,0.26);
        }

        .zfast-whatsapp h3,
        .zfast-whatsapp p {
          color: #ffffff;
        }

        .zfast-service-box {
          margin-top: 20px;
          padding: 24px;
          border-radius: 22px;
          background: linear-gradient(135deg, #092f49, #123f2a);
          color: #ffffff;
          border-left: 6px solid var(--green-2);
        }

        .zfast-service-box h3 {
          margin: 0 0 12px;
          font-size: 23px;
        }

        .zfast-service-box p {
          margin: 0 0 18px;
          line-height: 1.65;
          color: rgba(255,255,255,0.78);
          font-size: 15px;
        }

        .zfast-tags {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }

        .zfast-tags span {
          padding: 11px 12px;
          border-radius: 12px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.15);
          font-size: 13px;
          font-weight: 800;
          text-align: center;
        }

        .zfast-form-panel {
          padding: 34px;
        }

        .zfast-form-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          padding-bottom: 24px;
          margin-bottom: 26px;
          border-bottom: 1px solid #e3eee5;
        }

        .zfast-form-head h2 {
          margin: 0 0 8px;
          font-size: 32px;
          letter-spacing: -1px;
          color: var(--dark);
        }

        .zfast-form-head p {
          margin: 0;
          color: var(--text);
          line-height: 1.6;
          font-size: 15px;
        }

        .zfast-response-badge {
          padding: 10px 14px;
          border-radius: 12px;
          background: #eef8f0;
          color: var(--green);
          font-size: 13px;
          font-weight: 900;
          white-space: nowrap;
          border: 1px solid #d8eadb;
        }

        .zfast-form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .zfast-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .zfast-field.full {
          grid-column: 1 / -1;
        }

        .zfast-field label {
          font-size: 14px;
          font-weight: 900;
          color: #183326;
        }

        .zfast-field input,
        .zfast-field select,
        .zfast-field textarea {
          width: 100%;
          min-height: 54px;
          padding: 0 16px;
          border-radius: 14px;
          border: 1px solid #cfe1d1;
          background: #ffffff;
          outline: none;
          font-size: 15px;
          color: var(--dark);
          transition: 0.2s ease;
        }

        .zfast-field textarea {
          min-height: 122px;
          padding-top: 15px;
          resize: vertical;
          font-family: inherit;
        }

        .zfast-field input:focus,
        .zfast-field select:focus,
        .zfast-field textarea:focus {
          border-color: var(--green);
          box-shadow: 0 0 0 4px rgba(22, 115, 58, 0.12);
        }

        .zfast-submit-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
          margin-top: 28px;
          padding-top: 24px;
          border-top: 1px solid #e3eee5;
        }

        .zfast-submit-row p {
          margin: 0;
          max-width: 400px;
          font-size: 13px;
          line-height: 1.6;
          color: #687a70;
        }

        .zfast-submit-btn {
          min-height: 54px;
          padding: 0 30px;
          border: 0;
          border-radius: 14px;
          cursor: pointer;
          background: linear-gradient(135deg, var(--green), var(--green-2));
          color: #ffffff;
          font-size: 15px;
          font-weight: 900;
          box-shadow: 0 16px 32px rgba(22, 115, 58, 0.25);
          transition: 0.25s ease;
          white-space: nowrap;
        }

        .zfast-submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 20px 42px rgba(22, 115, 58, 0.33);
        }

        @media (max-width: 980px) {
          .zfast-dealer__top,
          .zfast-dealer__layout {
            grid-template-columns: 1fr;
          }

          .zfast-top-card {
            max-width: 520px;
          }
        }

        @media (max-width: 650px) {
          .zfast-dealer {
            padding: 56px 14px;
          }

          .zfast-dealer h1 {
            letter-spacing: -2px;
          }

          .zfast-dealer__intro {
            font-size: 16px;
          }

          .zfast-info-panel,
          .zfast-form-panel {
            padding: 18px;
            border-radius: 22px;
          }

          .zfast-form-head,
          .zfast-submit-row {
            flex-direction: column;
            align-items: stretch;
          }

          .zfast-form-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .zfast-tags {
            grid-template-columns: 1fr;
          }

          .zfast-submit-btn {
            width: 100%;
          }
        }
      `}</style>
      <section className="zfast-dealer">
        <div className="zfast-dealer__wrap">

          <div className="zfast-dealer__top">
            <div>
              <div className="zfast-kicker">Partnership</div>
              <h1>Grow with Zfast Agritech</h1>
              <p className="zfast-dealer__intro">
                Use this page for dealer, distributor, bulk feed, and edible oil enquiries. Our team can discuss product requirements, location, pack sizes, dispatch routes, documentation needs, and supply terms.
              </p>
            </div>

            <div className="zfast-top-card">
              <strong>Who can partner</strong>
              <span>
                Feed dealers, edible oil distributors, agriculture retailers, dairy network owners, and bulk suppliers.
              </span>
            </div>
          </div>

          <div className="zfast-dealer__layout">

            <aside className="zfast-info-panel">

              <div className="zfast-info-heading">
                <h2>Partner with Us</h2>
                <p>
                  Speak with our team to discuss location, pack sizes, and supply terms.
                </p>
              </div>

              <div className="zfast-dealer-card">
                <div className="zfast-dealer-card__icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.08 5.18 2 2 0 0 1 5.06 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.77.62 2.6a2 2 0 0 1-.45 2.11L9 10.66a16 16 0 0 0 4.34 4.34l1.23-1.23a2 2 0 0 1 2.11-.45c.83.29 1.7.5 2.6.62A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <h3>Sales & Support</h3>
                  <span>+91 98120 13300</span>
                </div>
              </div>

              <div className="zfast-dealer-card">
                <div className="zfast-dealer-card__icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <path d="m22 6-10 7L2 6"/>
                  </svg>
                </div>
                <div>
                  <h3>Email Enquiry</h3>
                  <span>info@zfastagritechindia.com</span>
                </div>
              </div>

              <a className="zfast-dealer-card zfast-whatsapp" href="https://wa.me/919812013300" target="_blank" rel="noreferrer">
                <div className="zfast-dealer-card__icon">
                  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                    <path d="M21 11.5a8.38 8.38 0 0 1-1.23 4.38A8.5 8.5 0 0 1 12.5 20a8.38 8.38 0 0 1-4.38-1.23L3 20l1.23-5.12A8.38 8.38 0 0 1 3 10.5 8.5 8.5 0 0 1 7.12 3.23 8.38 8.38 0 0 1 11.5 2h.5A8.5 8.5 0 0 1 21 10.5z"/>
                  </svg>
                </div>
                <div>
                  <h3>WhatsApp</h3>
                  <p>Quick enquiry for farmers, dealers, distributors, and buyers.</p>
                </div>
              </a>

              <div className="zfast-service-box">
                <h3>What to discuss</h3>
                <p>
                  Product requirement, location, pack size needs, dispatch route, and documentation requirements.
                </p>

                <div className="zfast-tags">
                  <span>Feed Dealers</span>
                  <span>Distributors</span>
                  <span>Bulk Supply</span>
                  <span>Retailers</span>
                </div>
              </div>

            </aside>

            <form className="zfast-form-panel" onSubmit={handleSubmit} noValidate>
              <div className="zfast-form-head">
                <div>
                  <h2>Partner Request</h2>
                  <p>
                    Fill in your details and our sales team will contact you shortly to discuss requirements.
                  </p>
                </div>
                <div className="zfast-response-badge">Response within 24 hours</div>
              </div>

              <div className="zfast-form-grid">

                <div className="zfast-field">
                  <label>First name *</label>
                  <input 
                    type="text" 
                    value={form.firstName} 
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })} 
                    placeholder="Enter first name" 
                    required 
                  />
                  {errors.firstName && <span className="text-xs text-red-600 font-semibold">{errors.firstName}</span>}
                </div>

                <div className="zfast-field">
                  <label>Last name</label>
                  <input 
                    type="text" 
                    value={form.lastName} 
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })} 
                    placeholder="Enter last name" 
                  />
                </div>

                <div className="zfast-field">
                  <label>Phone number *</label>
                  <input 
                    type="tel" 
                    value={form.phone} 
                    onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                    placeholder="Enter mobile number" 
                    required 
                  />
                  {errors.phone && <span className="text-xs text-red-600 font-semibold">{errors.phone}</span>}
                </div>

                <div className="zfast-field">
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={form.email} 
                    onChange={(e) => setForm({ ...form, email: e.target.value })} 
                    placeholder="Enter email address" 
                  />
                  {errors.email && <span className="text-xs text-red-600 font-semibold">{errors.email}</span>}
                </div>

                <div className="zfast-field">
                  <label>State *</label>
                  <input 
                    type="text" 
                    value={form.state} 
                    onChange={(e) => setForm({ ...form, state: e.target.value })} 
                    placeholder="Enter state" 
                    required 
                  />
                  {errors.state && <span className="text-xs text-red-600 font-semibold">{errors.state}</span>}
                </div>

                <div className="zfast-field">
                  <label>District *</label>
                  <input 
                    type="text" 
                    value={form.district} 
                    onChange={(e) => setForm({ ...form, district: e.target.value })} 
                    placeholder="Enter district" 
                    required 
                  />
                  {errors.district && <span className="text-xs text-red-600 font-semibold">{errors.district}</span>}
                </div>

                <div className="zfast-field full">
                  <label>Additional Details</label>
                  <textarea 
                    value={form.message} 
                    onChange={(e) => setForm({ ...form, message: e.target.value })} 
                    placeholder="Tell us about your expected volumes, current business, or any specific requirements..."
                  ></textarea>
                </div>

              </div>

              {globalError && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#ffebee', color: '#c62828', borderRadius: '12px', fontWeight: 'bold' }}>
                  {globalError}
                </div>
              )}

              {success && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '12px', fontWeight: 'bold' }}>
                  {success}
                </div>
              )}

              <div className="zfast-submit-row">
                <p>
                  By submitting this form, you agree to be contacted by the Zfast team regarding your partnership enquiry.
                </p>

                <button type="submit" disabled={loading} className="zfast-submit-btn" style={{ opacity: loading ? 0.75 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Sending...' : 'Submit Enquiry'}
                </button>
              </div>
            </form>

          </div>
        </div>
      </section>
    </PageShell>
  );
}
